"use strict";

import Analytics from "/js/util/analytics.js";
import Util from "/js/util/util.js";
import Global from "/js/core/global.js";
import Metrics from "/js/util/metrics.js";
import AppUtil from "/js/util/app-util.js";
import FadeUtil from "/js/util/fade-util.js";
import PrimerMaster from "/js/util/primermaster.js";
import UserLessons from "/js/core/user-lessons";
import AnimSequencer from "/js/util/anim-sequencer.js";
import Constants from '/js/core/constants.js';
import FocusUtil from '/js/util/focus-util.js';

/** 
 * Done view, which displays at tne completion of the lesson.
 *
 * Animates-in #greatJob anim and then animates-in #recapCta and #nextLesson.
 */
export default class DoneView {

  #$el = $('#doneView');
  #$greatJob = this.#$el.find('#doneGreatJob');
  #$recapCta = this.#$el.find('#doneRecapCta');
  #$backButton = this.#$el.find("#doneBackButton");
  #$nextLesson = this.#$el.find('#doneNextLesson');
  #$nextLessonButton = this.#$nextLesson.find("#doneNextLessonButton");
  #nextLessonId = null;

  constructor() {
    this.#$recapCta.on("click tap", e => this.#onRecapCtaClick(e));
    this.#$backButton.on("click tab", e => this.#onBackButton(e));
    this.#$nextLessonButton.on("click tap", e => this.#onNextLessonButtonClick(e));
    FocusUtil.doStandardTreatment(this.#$recapCta);
    FocusUtil.doStandardTreatment(this.#$backButton);
    FocusUtil.doStandardTreatment(this.#$nextLessonButton);
  }

  /**
   * Shows the done view, starting with the interstitial 'great job' view and
   * then the main views.
   *
   * @param fadeInToEndState Skip the whole animation sequence and just fade in
   *  the main views.
   */
  show(fadeInToEndState=false) {

    Analytics.dashboardButtonTrackingSource = "lesson_complete";

    UserLessons.saveCurrent();

    this.#nextLessonId = AppUtil.getNextUnfinishedLesson(Global.lessonId);

    this.#$recapCta.removeClass("noAnim");
    this.#$recapCta.hide();
    this.#$backButton.removeClass("noAnim");
    this.#$backButton.hide();
    this.#$nextLesson.removeClass("noAnim");
    this.#$nextLesson.hide();

    this.#populate();

    if (fadeInToEndState) {
      this.#showFinal(true);
      FadeUtil.fadeIn(this.#$el);
      return;
    }

    FadeUtil.fadeInNow(this.#$el);

    Util.windowUnselect();

    // Show #greatJobView, which will trigger its animation.
    AnimSequencer.listenForAnimationEnd(
        this.#$greatJob,
        () => this.#$greatJob.show(),
        () => this.#showFinal());
  }

  #showFinal(now=false) {
    this.#$greatJob.hide();

    // Recap CTA
    if (now) {
      this.#$recapCta.addClass("noAnim");
    }
    this.#$recapCta.show();  // Triggers css anim

    // Next lesson CTA
    const nextLessonExists = !!this.#nextLessonId;
    if (nextLessonExists) {
      if (now) {
        this.#$nextLesson.addClass("noAnim");
      }
      this.#$nextLesson.show();
    } else {
      this.#$nextLesson.hide();
    }

    // Back button
    if (Global.minicourseData && !nextLessonExists) {
      if (now) {
        this.#$backButton.addClass("noAnim");
      }
      this.#$backButton.show();  // Triggers css anim
    }

    this.isEnabled = true;
  }

  get $el() {
    return this.#$el;
  }

  hide(now=false) {
    if (now) {
      FadeUtil.fadeOutNow(this.#$el);
    } else {
      FadeUtil.fadeOut(this.#$el);
    }
  }

  set isEnabled(b) {
    if (b) {
      Util.setAriaHidden(this.#$el, false);
      this.#$recapCta.attr("tabindex", Constants.TABINDEX_CONTENT_GENERAL);
      this.#$backButton.attr("tabindex", Constants.TABINDEX_CONTENT_GENERAL + 1);
      this.#$nextLessonButton.attr("tabindex", Constants.TABINDEX_CONTENT_GENERAL + 2);
    } else {
      Util.setAriaHidden(this.#$el, true);
      this.#$recapCta.attr("tabindex", -1);
      this.#$backButton.attr("tabindex", -1);
      this.#$nextLessonButton.attr("tabindex", -1);
    }
  }

  resize() {
    // Adjust top position of cta lockup when size of window it too short :/
    // Rem, original css for #doneRecapCta is:  top: calc(50% - 75px)
    const threshold = Metrics.isMobileWidth ? 350 : 415; // hardcoded
    const overage = threshold - $(document).height();
    let topString = "";
    if (overage > 0) {
      const defaultOffset = 75;
      const finalOffset = defaultOffset + overage / 2;
      topString = `calc(50% - ${finalOffset}px)`;
    }
    this.#$recapCta.css("top", topString);
  }

  #populate() {

    // Recap - current lesson title
    const o = PrimerMaster.getLessonById(Global.lessonId);
    const title = o ? (o.title || "") : "";
    const $title = this.#$el.find("#doneRecapCtaLessonTitle");
    $title.text(title);

    // Recap - current lesson cover illustration
    const $recapIllo = this.#$el.find("#doneRecapCtaIllo");
    const path = AppUtil.makeFeatureImagePath(Global.lessonId);
    $recapIllo.attr("src", path);

    // Next lesson title
    if (this.#nextLessonId) {
      const o = PrimerMaster.getLessonById(this.#nextLessonId);
      const title = o ? (o.title || "") : "";
      const $title = this.#$el.find("#doneNextLessonTitle");
      $title.text(title);
    }
  }

  #onRecapCtaClick(e) {
    Analytics.trackLessonCompleteOpenRecap(UserLessons.totalCompletedLessonsCount, this.#nextLessonId);
    this.#$el.trigger("done-view-recap-button", this.#nextLessonId);
  }

  #onNextLessonButtonClick(e) {
    Analytics.trackLessonStartFromComplete(UserLessons.totalCompletedLessonsCount, this.#nextLessonId);
    Analytics.lessonTrackingSource = "lesson";
    this.#$el.trigger("next-lesson-button", this.#nextLessonId);
  }

  #onBackButton(e) {
    Analytics.trackLessonCompleteBackToMinicourse();
    this.#$el.trigger("done-view-back-to-dashboard-button");
  }
}
